import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../i18n';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { lang, setLang, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: t.nav.home, href: '#home' },
    { label: t.nav.services, href: '#services' },
    { label: t.nav.countries, href: '#countries' },
    { label: t.nav.about, href: '#about' },
    { label: t.nav.portfolio, href: '#portfolio' },
    { label: t.nav.testimonials, href: '#testimonials' },
    { label: t.nav.contact, href: '#contact' }
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const LangToggle = ({ variant }) => (
    <div className="flex items-center rounded-full border-2 border-[#E9782A]/30 overflow-hidden text-sm font-semibold" data-testid={`language-toggle-${variant}`}>
      <button
        onClick={() => setLang('en')}
        data-testid={`lang-en-button-${variant}`}
        className={`px-3 py-1 transition-colors duration-200 ${lang === 'en' ? 'bg-[#E9782A] text-white' : 'text-gray-600 hover:text-[#E9782A]'}`}
      >
        EN
      </button>
      <button
        onClick={() => setLang('fr')}
        data-testid={`lang-fr-button-${variant}`}
        className={`px-3 py-1 transition-colors duration-200 ${lang === 'fr' ? 'bg-[#E9782A] text-white' : 'text-gray-600 hover:text-[#E9782A]'}`}
      >
        FR
      </button>
    </div>
  );

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <a href="#home" className="flex items-center space-x-2" onClick={(e) => scrollToSection(e, '#home')}>
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#E9782A] to-[#d96820] flex items-center justify-center">
              <span className="text-white font-bold text-xl">F</span>
            </div>
            <span className="text-2xl font-bold text-gray-900">Fegga</span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="text-gray-700 hover:text-[#E9782A] transition-colors duration-200 font-medium"
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Right actions */}
          <div className="hidden lg:flex items-center space-x-4">
            <LangToggle variant="desktop" />
            <Button
              data-testid="header-cta-button"
              className="bg-[#E9782A] hover:bg-[#d96820] text-white px-6 py-2 rounded-lg transition-all duration-200 hover:shadow-lg"
              onClick={(e) => scrollToSection(e, '#contact')}
            >
              {t.header.cta}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center space-x-3">
            <LangToggle variant="mobile" />
            <button
              data-testid="mobile-menu-button"
              className="text-gray-700 hover:text-[#E9782A] transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden pb-4 animate-in slide-in-from-top duration-200">
            <nav className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="text-gray-700 hover:text-[#E9782A] transition-colors duration-200 font-medium py-2"
                >
                  {link.label}
                </a>
              ))}
              <Button
                className="bg-[#E9782A] hover:bg-[#d96820] text-white w-full py-2 rounded-lg transition-all duration-200"
                onClick={(e) => scrollToSection(e, '#contact')}
              >
                {t.header.cta}
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
